/**
 * Optimisations SEO globales pour le site Groupe Fabre Environnement
 */

export interface SEOAuditItem {
  category: 'critical' | 'important' | 'minor';
  title: string;
  description: string;
  status: 'good' | 'warning' | 'error';
  suggestion?: string;
}

export interface PageSEOData {
  url: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  hasCanonical: boolean;
  hasStructuredData: boolean;
  imageCount: number;
  internalLinks: number;
  loadTime: number;
}

// Validations SEO automatiques
export const validatePageSEO = (data: Partial<PageSEOData>): SEOAuditItem[] => {
  const issues: SEOAuditItem[] = [];

  // Validation du titre
  if (!data.title || data.title.length < 30) {
    issues.push({
      category: 'critical',
      title: 'Titre trop court',
      description: `Le titre fait ${data.title?.length || 0} caractères`,
      status: 'error',
      suggestion: 'Le titre doit faire entre 30 et 60 caractères pour un SEO optimal'
    });
  } else if (data.title.length > 60) {
    issues.push({
      category: 'important',
      title: 'Titre trop long',
      description: `Le titre fait ${data.title.length} caractères`,
      status: 'warning',
      suggestion: 'Le titre sera tronqué dans les résultats de recherche'
    });
  }

  // Validation de la description
  if (!data.description || data.description.length < 120) {
    issues.push({
      category: 'critical',
      title: 'Description trop courte',
      description: `La description fait ${data.description?.length || 0} caractères`,
      status: 'error',
      suggestion: 'La description doit faire entre 120 et 160 caractères'
    });
  } else if (data.description.length > 160) {
    issues.push({
      category: 'important',
      title: 'Description trop longue',
      description: `La description fait ${data.description.length} caractères`,
      status: 'warning',
      suggestion: 'La description sera tronquée dans les résultats'
    });
  }

  // Validation du H1
  if (!data.h1) {
    issues.push({
      category: 'critical',
      title: 'H1 manquant',
      description: 'Aucun titre H1 détecté sur la page',
      status: 'error',
      suggestion: 'Chaque page doit avoir un unique titre H1 descriptif'
    });
  }

  // Validation des mots-clés
  if (!data.keywords || data.keywords.length < 50) {
    issues.push({
      category: 'minor',
      title: 'Mots-clés insuffisants',
      description: 'Peu de mots-clés renseignés',
      status: 'warning',
      suggestion: 'Ajoutez des mots-clés pertinents pour le référencement'
    });
  }

  // Validation des données structurées
  if (!data.hasStructuredData) {
    issues.push({
      category: 'important',
      title: 'Données structurées manquantes',
      description: 'Aucune donnée Schema.org détectée',
      status: 'warning',
      suggestion: 'Ajoutez des données structurées pour améliorer l\'affichage dans les résultats'
    });
  }

  // Validation canonique
  if (!data.hasCanonical) {
    issues.push({
      category: 'important',
      title: 'URL canonique manquante',
      description: 'Aucune balise canonical détectée',
      status: 'warning',
      suggestion: 'Ajoutez une URL canonique pour éviter le contenu dupliqué'
    });
  }

  return issues;
};

// Générateur de mots-clés optimisés
export const generateOptimizedKeywords = (
  service: string,
  location?: string,
  metalType?: string
): string => {
  const baseKeywords = [
    'recyclage', 'valorisation déchets', 'Groupe Fabre', 'environnement'
  ];

  const serviceKeywords: { [key: string]: string[] } = {
    'location-bennes': ['location bennes', 'bennes déchets', 'conteneurs', 'évacuation déchets'],
    'achat-metaux': ['achat métaux', 'rachat ferraille', 'récupération métaux', 'métaux recyclage'],
    'demolition': ['démolition', 'déconstruction', 'travaux démolition', 'démolition bâtiment'],
    'valorisation': ['valorisation', 'traitement déchets', 'tri déchets', 'économie circulaire']
  };

  const locationKeywords = location ? [
    location,
    `${service} ${location}`,
    `déchets ${location}`,
    `recyclage ${location}`
  ] : [];

  const metalKeywords = metalType ? [
    metalType,
    `${metalType} recyclage`,
    `achat ${metalType}`,
    `prix ${metalType}`
  ] : [];

  return [
    ...baseKeywords,
    ...(serviceKeywords[service] || []),
    ...locationKeywords,
    ...metalKeywords
  ].join(', ');
};

// Optimisation des titres de page
export const generateOptimizedTitle = (
  service: string,
  location?: string,
  metalType?: string
): string => {
  const company = "Groupe Fabre Environnement";
  
  const serviceTitles: { [key: string]: string } = {
    'location-bennes': 'Location de Bennes',
    'achat-metaux': 'Achat de Métaux',
    'demolition': 'Démolition',
    'valorisation': 'Valorisation des Déchets'
  };

  let title = serviceTitles[service] || service;
  
  if (metalType) {
    title += ` ${metalType.charAt(0).toUpperCase() + metalType.slice(1)}`;
  }
  
  if (location) {
    title += ` ${location.charAt(0).toUpperCase() + location.slice(1)}`;
  }
  
  title += ` | ${company}`;
  
  // S'assurer que le titre fait entre 50-60 caractères
  if (title.length > 60) {
    title = title.replace(' | Groupe Fabre Environnement', ' | Groupe Fabre');
  }
  
  return title;
};

// Optimisation des descriptions
export const generateOptimizedDescription = (
  service: string,
  location?: string,
  metalType?: string
): string => {
  const baseDescriptions: { [key: string]: string } = {
    'location-bennes': 'Location de bennes pour déchets avec livraison rapide',
    'achat-metaux': 'Achat de métaux aux meilleurs prix du marché',
    'demolition': 'Services de démolition professionnels et écologiques',
    'valorisation': 'Valorisation et recyclage de tous types de déchets'
  };

  let description = baseDescriptions[service] || `Services de ${service}`;
  
  if (location) {
    description += ` dans le ${location}`;
  }
  
  if (metalType) {
    description += ` - Spécialiste ${metalType}`;
  }
  
  description += '. Groupe Fabre Environnement, expert recyclage depuis 25+ ans. Devis gratuit.';
  
  // S'assurer que la description fait entre 140-160 caractères
  if (description.length > 160) {
    description = description.substring(0, 157) + '...';
  }
  
  return description;
};

// Validation des images pour le SEO
export const validateImageSEO = (images: { src: string; alt?: string; title?: string }[]) => {
  const issues: SEOAuditItem[] = [];
  
  const imagesWithoutAlt = images.filter(img => !img.alt || img.alt.length < 5);
  if (imagesWithoutAlt.length > 0) {
    issues.push({
      category: 'important',
      title: 'Images sans texte alternatif',
      description: `${imagesWithoutAlt.length} image(s) sans alt text descriptif`,
      status: 'warning',
      suggestion: 'Ajoutez des descriptions alt détaillées pour l\'accessibilité et le SEO'
    });
  }
  
  const imagesWithoutTitle = images.filter(img => !img.title);
  if (imagesWithoutTitle.length > 0) {
    issues.push({
      category: 'minor',
      title: 'Images sans titre',
      description: `${imagesWithoutTitle.length} image(s) sans attribut title`,
      status: 'warning',
      suggestion: 'Ajoutez des titres descriptifs aux images importantes'
    });
  }
  
  return issues;
};

// Configuration des priorités d'indexation
export const getPagePriority = (url: string): number => {
  if (url === '/') return 1.0;
  if (url.includes('/nos-services/')) return 0.9;
  if (url.includes('/contact') || url.includes('/sites')) return 0.9;
  if (url.includes('/valorisation/')) return 0.8;
  if (url.includes('/nos-metiers/')) return 0.7;
  if (url.includes('/legal/')) return 0.3;
  return 0.6;
};

// Fréquence de mise à jour recommandée
export const getChangeFrequency = (url: string): string => {
  if (url === '/') return 'weekly';
  if (url.includes('/blog')) return 'daily';
  if (url.includes('/estimation') || url.includes('/contact')) return 'monthly';
  if (url.includes('/legal/')) return 'yearly';
  return 'monthly';
};

// Audit complet d'une page
export const auditPageSEO = async (url: string): Promise<SEOAuditItem[]> => {
  const issues: SEOAuditItem[] = [];
  
  try {
    // Simuler la récupération des données de la page
    const pageData: Partial<PageSEOData> = {
      url,
      hasCanonical: true, // À vérifier dynamiquement
      hasStructuredData: true, // À vérifier dynamiquement
      imageCount: 0,
      internalLinks: 0
    };
    
    // Ajouter les validations de base
    issues.push(...validatePageSEO(pageData));
    
    // Ajouter des vérifications spécifiques selon le type de page
    if (url.includes('/nos-services/')) {
      if (!url.includes('location-bennes') && !url.includes('achat-metaux') && !url.includes('demolition')) {
        issues.push({
          category: 'minor',
          title: 'URL non optimisée',
          description: 'L\'URL pourrait être plus descriptive',
          status: 'warning',
          suggestion: 'Utilisez des URLs descriptives avec mots-clés'
        });
      }
    }
    
  } catch (error) {
    issues.push({
      category: 'critical',
      title: 'Erreur d\'audit',
      description: 'Impossible d\'analyser la page',
      status: 'error',
      suggestion: 'Vérifiez la validité de l\'URL et la structure de la page'
    });
  }
  
  return issues;
};